import React, { useState } from "react";
import { Box, Button, Typography, Paper, Divider } from "@mui/material";
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { tokens } from "../../../theme";
import AddNumberModal from "./components/AddNumberModal";
import PersonalisationModal from "./components/PersonalisationModal";
import PersonaliseChoiceModal from "./components/PersonaliseChoiceModal";
import DatePicker from "./components/DatePicker";
import LocationPicker from "./components/LocationPicker";
import TimePicker from "./components/TimePicker";
import apiService from "../../../service/Api";
import ThankYouModal from "./components/ThankYouModalToast";

interface Deceased {
  salutation?: string;
  first_name: string;
  second_name?: string;
  surname: string;
  nickname?: string;
  gender?: string;
  date_of_birth?: string;
  date_of_death?: string;
  relationship_to_deceased?: string;
  religion?: string;
  denomination?: string;
  orbituary?: string;
  source_of_record?: string;
}

interface Contact {
  first_name: string;
  last_name?: string;
  phone: string;
}

interface QuotePayload {
  deceased: Deceased;
  contact: Contact;
  deceased_id?: number | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
}

const ServiceQuoteForm = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({
    when: "",
    where: "",
    whatTime: "",
    whatsappNumber: "",
    fullName: "",
    belovedName: "",
    churchName: "",
    burialLocation: "",
    photo: null as File | null,
  });

  const [currentModal, setCurrentModal] = useState<
    "whatsapp" | "Personalise" | "details" | null
  >(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const [whenAnchor, setWhenAnchor] = useState<HTMLElement | null>(null);
  const [whereAnchor, setWhereAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);

  const [showThankYou, setShowThankYou] = useState(false);

  const splitFullName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 0) {
      return { first_name: "", last_name: "" };
    }

    if (parts.length === 1) {
      return { first_name: parts[0], last_name: "" };
    }

    return {
      first_name: parts.slice(0, -1).join(" "),
      last_name: parts[parts.length - 1],
    };
  };

  const normalizeName = (name: string) =>
    name
      .split(" ")
      .map((n) => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase())
      .join(" ");

  // Reset form
  const resetForm = () => {
    setFormData({
      when: "",
      where: "",
      whatTime: "",
      whatsappNumber: "",
      fullName: "",
      belovedName: "",
      churchName: "",
      burialLocation: "",
      photo: null,
    });
    setPhotoPreview(null);
    setCurrentModal(null);
    setWhenAnchor(null);
    setWhereAnchor(null);
    setTimeAnchor(null);
  };

  // Step handlers
  const handleGetQuote = () => {
    if (!formData.when || !formData.where || !formData.whatTime) {
      toast.error("Please fill in all event details.");
      return;
    }
    setCurrentModal("whatsapp");
  };

  const handleWhatsAppSubmit = () => {
    if (!formData.fullName.trim().includes(" ")) {
      toast.error("Please enter your full name.");
      return;
    }

    if (!formData.whatsappNumber.trim()) {
      toast.error("Please enter a valid WhatsApp number.");
      return;
    }
    setCurrentModal("Personalise");
  };

  const submitMinimalQuote = async () => {
    const eventDatetime = `${formData.when}T${formData.whatTime}:00`;
    const fullName = formData.fullName;
    const phone = formData.whatsappNumber;
    const { first_name, last_name } = splitFullName(fullName);

    const payload: QuotePayload = {
      deceased: {
        first_name: "Not",
        surname: "provided",
      },
      contact: {
        first_name: normalizeName(first_name),
        last_name: normalizeName(last_name),
        phone: phone,
      },
      event_datetime: eventDatetime,
      venue_name: formData.where,
      venue_latitude: "0.0",
      venue_longitude: "0.0",
    };

    try {
      await apiService.post<QuotePayload>(
        "/api/bookings/quote-request/",
        payload
      );
      setShowThankYou(true);
      resetForm();
    } catch (err) {
      toast.error(
        (err as any)?.response?.data?.message ||
          "Failed to submit quote request"
      );
    }
  };

  const handlePersonaliseNo = () => {
    submitMinimalQuote();
  };

  const handlePersonaliseYes = () => {
    setCurrentModal("details");
  };

  // Submit quote to backend

  const handleDetailsSubmit = async () => {
    const {
      where,
      belovedName,
      churchName,
      burialLocation,
      fullName,
      whatsappNumber,
    } = formData;

    // Validation
    if (where === "Church") {
      if (!belovedName.trim() || !churchName.trim()) {
        toast.error("Please fill in all required personalization fields.");
        return;
      }
    } else if (where === "Burial Ground") {
      if (!burialLocation.trim()) {
        toast.error("Please specify the burial ground location.");
        return;
      }
    } else {
      if (!belovedName.trim()) {
        toast.error("Please enter the name of the beloved.");
        return;
      }
    }

    // Construct event datetime
    const eventDatetime = `${formData.when}T${formData.whatTime}:00`;
    const { first_name: deceasedFirstName, last_name: deceasedSurname } =
      splitFullName(belovedName || "John Doe");

    const { first_name: contactFirst, last_name: contactLast } =
      splitFullName(fullName);

    // Prepare payload
    const payload: QuotePayload = {
      deceased: {
        first_name: normalizeName(deceasedFirstName),
        surname: normalizeName(deceasedSurname),
      },
      contact: {
        first_name: normalizeName(contactFirst),
        last_name: normalizeName(contactLast),
        phone: whatsappNumber,
      },
      event_datetime: eventDatetime,
      venue_name: formData.where,
      venue_latitude: "0.0",
      venue_longitude: "0.0",
    };

    try {
      const response = await apiService.post<QuotePayload>(
        "/api/bookings/quote-request/",
        payload
      );

      setShowThankYou(true);
      resetForm();
    } catch (err) {
      toast.error(
        (err as any)?.response?.data?.message || "Something went wrong"
      );
    }
  };

  const handleSkipDetails = async () => {
    submitMinimalQuote();
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Photo must be under 10MB.");
        return;
      }
      setFormData({ ...formData, photo: file });
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const closeModal = () => setCurrentModal(null);

  const formatDate = (date: string) => {
    if (!date) return "Add dates";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "Add time";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const whereBoxRef = React.useRef<HTMLDivElement>(null);
  const timeBoxRef = React.useRef<HTMLDivElement>(null);

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 2, md: 12 }, px: 2 }}>
      {/* Header */}
      <Box
        sx={{
          textAlign: "center",
          mb: { xs: 3, md: 5 },
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: colors.primary[400],
            fontSize: { xs: "1.75rem", md: "2.5rem" },
          }}
        >
          Fast Quote: Funeral Live Stream Pricing
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            mt: 1,
            mb: 8,
            fontSize: { xs: "1rem", md: "1.25rem" },
          }}
        >
          Fill in your event details and receive a Personalised quote via
          WhatsApp
        </Typography>
      </Box>

      {/* Main Form Card */}
      <Paper
        elevation={8}
        sx={{
          maxWidth: "1000px",
          mx: "auto",
          borderRadius: { xs: 4, md: 50 },
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
          pl: { xs: 2, md: 0 },
          pr: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
          }}
        >
          {/* When Section */}
          <Box
            onClick={(e) => setWhenAnchor(e.currentTarget)}
            sx={{
              flex: 1,
              p: 3,
              cursor: "pointer",
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <CalendarIcon sx={{ color: "text.secondary" }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  When
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: formData.when ? "text.primary" : "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {formatDate(formData.when)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          <Divider sx={{ display: { xs: "block", md: "none" } }} />

          {/* Where Section */}
          <Box
            ref={whereBoxRef}
            onClick={(e) => setWhereAnchor(e.currentTarget)}
            sx={{
              flex: 1,
              p: 3,
              cursor: "pointer",
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <LocationIcon sx={{ color: "text.secondary" }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  Where
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: formData.where ? "text.primary" : "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {formData.where || "Search destinations"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          <Divider sx={{ display: { xs: "block", md: "none" } }} />

          {/* What Time Section */}
          <Box
            ref={timeBoxRef}
            onClick={(e) => setTimeAnchor(e.currentTarget)}
            sx={{
              flex: 1,
              p: 3,
              cursor: "pointer",
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <ScheduleIcon sx={{ color: "text.secondary" }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  What Time
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: formData.whatTime
                      ? "text.primary"
                      : "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {formatTime(formData.whatTime)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          <Divider sx={{ display: { xs: "block", md: "none" } }} />

          {/* Get Quote Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, md: 2 },
            }}
          >
            <Button
              variant="contained"
              onClick={handleGetQuote}
              startIcon={<SearchIcon />}
              fullWidth
              sx={{
                background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`,
                color: "white",
                px: { xs: 2, md: 6 },
                py: { xs: 2, md: 2.8 },
                borderRadius: { xs: 2, md: 50 },
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                transition: "all 0.3s ease",
                animation: "breathing 4.5s ease-in-out infinite",
                maxWidth: { md: 300 },
                width: { xs: "100%", md: "auto" },
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                  animation: "none",
                },
                "@keyframes breathing": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.07)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            >
              Get Estimate
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Custom Pickers */}
      <DatePicker
        open={Boolean(whenAnchor)}
        anchorEl={whenAnchor}
        onClose={() => setWhenAnchor(null)}
        value={formData.when}
        onChange={(date) => {
          setFormData({ ...formData, when: date });
          setWhenAnchor(null);
          if (whereBoxRef.current) {
            setWhereAnchor(whereBoxRef.current);
          }
        }}
        disablePast={true}
        showFlexibleDates={true}
      />

      <LocationPicker
        open={Boolean(whereAnchor)}
        anchorEl={whereAnchor}
        onClose={() => setWhereAnchor(null)}
        value={formData.where}
        onChange={(location) => {
          setFormData({ ...formData, where: location });
          setWhereAnchor(null);
          if (timeBoxRef.current) {
            setTimeAnchor(timeBoxRef.current);
          }
        }}
      />

      <TimePicker
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        value={formData.whatTime}
        onChange={(time) => {
          setFormData({ ...formData, whatTime: time });
          setTimeAnchor(null);
        }}
        format="12h"
      />

      {/* Modals */}
      <AddNumberModal
        open={currentModal === "whatsapp"}
        onClose={closeModal}
        whatsappNumber={formData.whatsappNumber}
        onNumberChange={(val) =>
          setFormData((prev) => ({ ...prev, whatsappNumber: val }))
        }
        fullName={formData.fullName}
        onFullNameChange={(val) =>
          setFormData((prev) => ({ ...prev, fullName: val }))
        }
        onSubmit={handleWhatsAppSubmit}
      />

      <PersonaliseChoiceModal
        open={currentModal === "Personalise"}
        onClose={closeModal}
        onYes={handlePersonaliseYes}
        onNo={handlePersonaliseNo}
      />

      <PersonalisationModal
        open={currentModal === "details"}
        onClose={closeModal}
        where={formData.where}
        belovedName={formData.belovedName}
        churchName={formData.churchName}
        burialLocation={formData.burialLocation}
        onBelovedNameChange={(val) =>
          setFormData((prev) => ({ ...prev, belovedName: val }))
        }
        onChurchNameChange={(val) =>
          setFormData((prev) => ({ ...prev, churchName: val }))
        }
        onBurialLocationChange={(val) =>
          setFormData((prev) => ({ ...prev, burialLocation: val }))
        }
        onPhotoUpload={handlePhotoUpload}
        photoPreview={photoPreview}
        onSubmit={handleDetailsSubmit}
        onSkip={handleSkipDetails}
      />
      <ThankYouModal
        open={showThankYou}
        onClose={() => {
          setShowThankYou(false);
          setCurrentModal(null);
        }}
      />
    </Box>
  );
};

export default ServiceQuoteForm;
